<!-- eslint-disable vue/v-slot-style -->
<script>
import { mapGetters } from 'vuex';
import { useAlert } from 'dashboard/composables';
import { useAgentsList } from 'dashboard/composables/useAgentsList';
import MultiselectDropdown from 'shared/components/ui/MultiselectDropdown.vue';
import MultiselectDropdownItems from 'shared/components/ui/MultiselectDropdownItems.vue';
import ConversationLabels from './labels/LabelBox.vue';
import Avatar from 'next/avatar/Avatar.vue';
import NextButton from 'dashboard/components-next/button/Button.vue';

export default {
  components: {
    MultiselectDropdown,
    MultiselectDropdownItems,
    ConversationLabels,
    Avatar,
    NextButton,
  },
  props: {
    conversationId: {
      type: [Number, String],
      required: true,
    },
  },
  setup() {
    const { agentsList } = useAgentsList(false);
    return {
      agentsList,
    };
  },
  data() {
    return {
      showAddParticipants: false,
      isUpdatingParticipants: false,
    };
  },
  computed: {
    ...mapGetters({
      currentChat: 'getSelectedChat',
      currentUser: 'getCurrentUser',
      teams: 'teams/getTeams',
      uiSettings: 'getUISettings',
    }),
    // SiteUp — Modo Foco: caixas fixas (Participantes/Time/Etiquetas) que o
    // usuário escolheu ocultar (ui_settings.conversation_pinned_hidden_items).
    pinnedHiddenItems() {
      const hidden = this.uiSettings?.conversation_pinned_hidden_items;
      return Array.isArray(hidden) ? hidden : [];
    },
    inboxId() {
      return this.currentChat?.inbox_id || null;
    },
    watchersFromStore() {
      return (
        this.$store.getters['conversationWatchers/getByConversationId'](
          this.conversationId
        ) || []
      );
    },
    assignedAgent() {
      return this.currentChat?.meta?.assignee || null;
    },
    assigneeId() {
      return this.assignedAgent?.id != null
        ? Number(this.assignedAgent.id)
        : null;
    },
    /**
     * All conversation participants (watchers + assignee if missing from list).
     * Assignee row shows the "Atribuído" subtitle (no colored avatar ring).
     */
    displayParticipants() {
      const byId = new Map();
      const assignee = this.assignedAgent;
      if (assignee?.id != null) {
        byId.set(Number(assignee.id), {
          ...assignee,
          isAssignee: true,
        });
      }
      this.watchersFromStore.forEach(participant => {
        const id = Number(participant.id);
        const existing = byId.get(id);
        byId.set(id, {
          ...(existing || {}),
          ...participant,
          isAssignee: this.assigneeId === id,
        });
      });
      return Array.from(byId.values());
    },
    hasAnAssignedTeam() {
      return !!this.currentChat?.meta?.team;
    },
    /** Hide Time box entirely when the account has no teams. */
    hasTeams() {
      return Array.isArray(this.teams) && this.teams.length > 0;
    },
    teamsList() {
      if (this.hasAnAssignedTeam) {
        return [
          { id: 0, name: this.$t('TEAMS_SETTINGS.LIST.NONE') },
          ...this.teams,
        ];
      }
      return this.teams;
    },
    assignedTeam: {
      get() {
        return this.currentChat.meta.team;
      },
      set(team) {
        const conversationId = this.currentChat.id;
        const teamId = team ? team.id : 0;
        this.$store.dispatch('setCurrentChatTeam', { team, conversationId });
        this.$store
          .dispatch('assignTeam', { conversationId, teamId })
          .then(() => {
            useAlert(this.$t('CONVERSATION.CHANGE_TEAM'));
          });
      },
    },
    /** Mockup empty state: users icon + "Nenhum" (not blank placeholder). */
    teamSelectorItem() {
      if (this.assignedTeam?.id) {
        return { ...this.assignedTeam, icon: 'i-ri-group-line' };
      }
      return {
        id: 0,
        name: this.$t('TEAMS_SETTINGS.LIST.NONE'),
        icon: 'i-ri-group-line',
      };
    },
  },
  watch: {
    conversationId: {
      immediate: true,
      handler() {
        this.fetchParticipants();
        this.fetchAssignableAgents();
      },
    },
    inboxId() {
      this.fetchAssignableAgents();
    },
  },
  mounted() {
    this.$store.dispatch('agents/get');
    this.fetchAssignableAgents();
  },
  methods: {
    fetchParticipants() {
      if (!this.conversationId) return;
      this.$store.dispatch('conversationWatchers/show', {
        conversationId: this.conversationId,
      });
    },
    fetchAssignableAgents() {
      if (!this.inboxId) return;
      this.$store.dispatch('inboxAssignableAgents/fetch', [this.inboxId]);
    },
    isCurrentUser(userId) {
      return Number(userId) === Number(this.currentUser?.id);
    },
    displayName(user) {
      return this.isCurrentUser(user.id)
        ? this.$t('CONVERSATION_PARTICIPANTS.YOU')
        : user.name;
    },
    onClickAssignTeam(selectedItemTeam) {
      if (!selectedItemTeam || Number(selectedItemTeam.id) === 0) {
        this.assignedTeam = null;
        return;
      }
      if (this.assignedTeam && this.assignedTeam.id === selectedItemTeam.id) {
        this.assignedTeam = null;
      } else {
        this.assignedTeam = selectedItemTeam;
      }
    },
    toggleAddParticipants() {
      this.showAddParticipants = !this.showAddParticipants;
      if (this.showAddParticipants) {
        this.fetchAssignableAgents();
      }
    },
    closeAddParticipants() {
      this.showAddParticipants = false;
    },
    async updateParticipantIds(userIds) {
      let alertMessage = this.$t(
        'CONVERSATION_PARTICIPANTS.API.SUCCESS_MESSAGE'
      );
      try {
        await this.$store.dispatch('conversationWatchers/update', {
          conversationId: this.conversationId,
          userIds,
        });
      } catch (error) {
        alertMessage =
          error?.message ||
          this.$t('CONVERSATION_PARTICIPANTS.API.ERROR_MESSAGE');
      } finally {
        useAlert(alertMessage);
      }
      this.fetchParticipants();
    },
    async onToggleParticipant(agent) {
      if (this.isUpdatingParticipants || !agent?.id) return;
      const agentId = Number(agent.id);
      const isSelected = this.watchersFromStore.some(
        participant => Number(participant.id) === agentId
      );

      if (isSelected) {
        await this.removeFromConversation(agent);
        return;
      }

      this.isUpdatingParticipants = true;
      try {
        const userIds = [
          ...this.watchersFromStore.map(participant => participant.id),
          agent.id,
        ];
        await this.updateParticipantIds(userIds);
      } finally {
        this.isUpdatingParticipants = false;
      }
    },
    /**
     * Remove from participants. If they were the assignee, promote the next
     * remaining participant or leave unassigned (same contract as header).
     */
    async removeFromConversation(user) {
      if (this.isUpdatingParticipants || !user?.id) return;

      const removedId = Number(user.id);
      const remaining = this.watchersFromStore.filter(
        participant => Number(participant.id) !== removedId
      );
      // If assignee wasn't in watchers, remaining is still correct for watchers API
      const wasAssignee = this.assigneeId === removedId;
      const nextAssignee = wasAssignee
        ? remaining[0] ||
          this.displayParticipants.find(p => Number(p.id) !== removedId) ||
          null
        : null;
      const removingSelf = this.isCurrentUser(removedId);

      this.isUpdatingParticipants = true;
      let alertMessage = removingSelf
        ? this.$t('CONVERSATION_PARTICIPANTS.LEAVE_SUCCESS')
        : this.$t('CONVERSATION_PARTICIPANTS.REMOVE_SUCCESS', {
            name: user.name,
          });

      try {
        await this.$store.dispatch('conversationWatchers/update', {
          conversationId: this.conversationId,
          userIds: remaining.map(participant => participant.id),
        });

        if (wasAssignee) {
          await this.$store.dispatch('assignAgent', {
            conversationId: this.conversationId,
            agentId: nextAssignee ? nextAssignee.id : null,
          });
          if (nextAssignee) {
            alertMessage = this.$t(
              'CONVERSATION_PARTICIPANTS.REMOVE_ASSIGNEE_PROMOTED',
              {
                removed: user.name,
                next: this.displayName(nextAssignee),
              }
            );
          } else {
            alertMessage = this.$t(
              'CONVERSATION_PARTICIPANTS.REMOVE_ASSIGNEE_UNASSIGNED',
              {
                name: user.name,
              }
            );
          }
        }
      } catch (error) {
        alertMessage =
          error?.message ||
          this.$t('CONVERSATION_PARTICIPANTS.API.ERROR_MESSAGE');
      } finally {
        this.isUpdatingParticipants = false;
        useAlert(alertMessage);
      }
    },
  },
};
</script>

<template>
  <!-- Compact nested cards (mockup): Participantes / Time / Etiquetas — tight spacing -->
  <div class="flex flex-col divide-y divide-n-weak">
    <!--
      clickaway MUST wrap trigger + popover. Putting it only on the dropdown
      made "+ Adicionar" open-then-immediately-close on the same click.
      SiteUp: lista + Remover (sem chips/ring verde no atribuído).
    -->
    <div
      v-if="!pinnedHiddenItems.includes('participants')"
      v-on-clickaway="closeAddParticipants"
      class="relative px-0.5 py-1"
    >
      <span class="mb-1.5 block text-xs font-medium text-n-slate-11">
        {{ $t('CONVERSATION_PARTICIPANTS.SIDEBAR_MENU_TITLE') }}
      </span>

      <ul
        v-if="displayParticipants.length"
        class="m-0 flex list-none flex-col gap-0.5 p-0"
      >
        <li
          v-for="participant in displayParticipants"
          :key="participant.id"
          class="flex items-center gap-2 py-1"
        >
          <Avatar
            :name="participant.name"
            :src="participant.thumbnail"
            :size="28"
            rounded-full
            class="shrink-0"
          />
          <div class="min-w-0 flex-1">
            <p
              class="m-0 truncate text-sm font-medium leading-tight text-n-slate-12"
            >
              {{ displayName(participant) }}
            </p>
            <p
              v-if="participant.isAssignee"
              class="m-0 truncate text-[11px] leading-tight text-n-slate-10"
            >
              {{ $t('CONVERSATION_PARTICIPANTS.ASSIGNEE_BADGE') }}
            </p>
          </div>
          <button
            type="button"
            class="shrink-0 px-0.5 text-xs font-normal text-n-slate-10 transition-colors hover:text-n-slate-12 disabled:opacity-40"
            :disabled="isUpdatingParticipants"
            :aria-label="
              isCurrentUser(participant.id)
                ? $t('CONVERSATION_PARTICIPANTS.LEAVE')
                : $t('CONVERSATION_PARTICIPANTS.REMOVE_PERSON', {
                    name: displayName(participant),
                  })
            "
            :title="
              isCurrentUser(participant.id)
                ? $t('CONVERSATION_PARTICIPANTS.LEAVE')
                : $t('CONVERSATION_PARTICIPANTS.REMOVE_HINT', {
                    name: displayName(participant),
                  })
            "
            @click.stop="removeFromConversation(participant)"
          >
            {{ $t('CONVERSATION_PARTICIPANTS.REMOVE_ACTION') }}
          </button>
        </li>
      </ul>
      <p v-else class="m-0 py-0.5 text-xs text-n-slate-10">
        {{ $t('CONVERSATION_PARTICIPANTS.NO_PARTICIPANTS_TEXT') }}
      </p>

      <div class="mt-1.5 border-t border-n-weak pt-1.5">
        <button
          type="button"
          class="inline-flex items-center text-xs font-medium text-n-blue-11 transition-colors hover:text-n-blue-10 disabled:opacity-40"
          :aria-expanded="showAddParticipants"
          :aria-label="$t('CONVERSATION_PARTICIPANTS.ADD_SHORT')"
          @click.stop="toggleAddParticipants"
        >
          {{ $t('CONVERSATION_PARTICIPANTS.ADD_SHORT') }}
        </button>
      </div>

      <div
        v-if="showAddParticipants"
        class="absolute start-0 top-full z-[9999] mt-1 box-border w-full rounded-xl border border-n-strong bg-n-alpha-3 p-2 shadow-lg backdrop-blur-[100px]"
      >
        <div class="mb-1 flex items-center justify-between">
          <h4
            class="m-0 overflow-hidden text-ellipsis whitespace-nowrap text-sm text-n-slate-12"
          >
            {{ $t('CONVERSATION_PARTICIPANTS.ADD_PARTICIPANTS') }}
          </h4>
          <NextButton
            ghost
            slate
            xs
            icon="i-ri-close-line"
            @click="closeAddParticipants"
          />
        </div>
        <MultiselectDropdownItems
          :options="agentsList"
          :selected-items="watchersFromStore"
          has-thumbnail
          @select="onToggleParticipant"
        />
      </div>
    </div>

    <!-- Time atribuído: só se a conta tiver pelo menos 1 time -->
    <div
      v-if="hasTeams && !pinnedHiddenItems.includes('team')"
      class="flex flex-col gap-1 px-0.5 py-1"
    >
      <span class="text-xs font-medium text-n-slate-11">
        {{ $t('CONVERSATION_SIDEBAR.TEAM_LABEL') }}
      </span>
      <MultiselectDropdown
        class="!mb-0"
        :options="teamsList"
        :selected-item="teamSelectorItem"
        :has-thumbnail="false"
        :multiselector-title="$t('AGENT_MGMT.MULTI_SELECTOR.TITLE.TEAM')"
        :multiselector-placeholder="
          $t('CONVERSATION_SIDEBAR.SELECT.PLACEHOLDER')
        "
        :no-search-result="
          $t('AGENT_MGMT.MULTI_SELECTOR.SEARCH.NO_RESULTS.TEAM')
        "
        :input-placeholder="
          $t('AGENT_MGMT.MULTI_SELECTOR.SEARCH.PLACEHOLDER.TEAM')
        "
        @select="onClickAssignTeam"
      />
    </div>

    <!-- Etiquetas -->
    <div
      v-if="!pinnedHiddenItems.includes('labels')"
      class="flex flex-col gap-1 px-0.5 py-1"
    >
      <span class="text-xs font-medium text-n-slate-11">
        {{ $t('CONVERSATION_SIDEBAR.LABELS_LABEL_SHORT') }}
      </span>
      <ConversationLabels :conversation-id="conversationId" />
    </div>
  </div>
</template>
